
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { SecureInput } from "@/components/security/SecureInput";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { emailSchema, nameSchema } from "@/lib/security/input-validation";
import { trackEvent } from "@/lib/analytics";
import { useOAuthSignup } from "@/hooks/use-oauth-signup";
import { trackRefgrowEvent } from "@/lib/refgrow";
import { isExistingAccountError, getAuthErrorTitle, getAuthErrorMessage } from "@/lib/utils/auth-error-handling";
import { ExistingAccountDialog } from "./ExistingAccountDialog";
import { errorHandler } from "@/lib/error-handler";
import { safeMixpanelTrack } from "@/lib/mixpanel-utils";

interface SignUpFormData {
  email: string;
  password: string;
  fullName: string;
}

export function SignUpForm({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmissionRef = useRef<number>(0);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { 
    signUpWithOAuth, 
    loading: googleLoading, 
    showExistingAccountDialog, 
    handleExistingAccountSignIn, 
    closeDialog 
  } = useOAuthSignup({
    provider: 'google',
    onSuccess: () => {
      // OAuth flow started successfully
      console.log('Google OAuth signup initiated');
    },
    onError: (error) => {
      console.error('Google OAuth signup error:', error);
    }
  });
  
  const form = useForm<SignUpFormData>({
    defaultValues: {
      email: '',
      password: '',
      fullName: ''
    }
  });

  const onSubmit = async (data: SignUpFormData) => {
    // Prevent multiple simultaneous submissions
    if (isSubmitting || loading) {
      console.log("[SignUpForm] Submission already in progress, ignoring duplicate submission");
      return;
    }
    
    // Debounce rapid submissions (prevent multiple calls within 1 second)
    const now = Date.now();
    if (now - lastSubmissionRef.current < 1000) {
      console.log("[SignUpForm] Submission too soon after last attempt, ignoring");
      return;
    }
    lastSubmissionRef.current = now;
    
    try {
      setIsSubmitting(true);
      setLoading(true);
      
      // Validate inputs
      const emailValidation = emailSchema.safeParse(data.email);
      const nameValidation = nameSchema.safeParse(data.fullName);
      
      if (!emailValidation.success) {
        throw new Error("Invalid email format");
      }
      if (!nameValidation.success) {
        throw new Error("Invalid name format");
      }
      
      const { data: signUpData, error } = await supabase.auth.signUp({
        email: emailValidation.data,
        password: data.password,
        options: {
          data: {
            full_name: nameValidation.data,
          },
          emailRedirectTo: `${window.location.origin}/onboarding`
        },
      });

      if (error) throw error;

      // Check if user is immediately signed in (no email confirmation required)
      const { data: { session } } = await supabase.auth.getSession();
      
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const utmCampaign = urlParams.get('utm_campaign');

      if (session?.user) {
        // Track successful signup with Refgrow
        trackRefgrowEvent(0, 'signup', session.user.email);

        // Track signup in our analytics
        await trackEvent({
          name: 'user_signup',
          properties: {
            signupMethod: 'email',
            userId: session.user.id,
          }
        });

        // Track Sign Up event in Mixpanel
        safeMixpanelTrack('Sign Up', {
          user_id: session.user.id,
          email: session.user.email || '',
          signup_method: 'email',
          utm_source: utmSource || null,
          utm_medium: utmMedium || null,
          utm_campaign: utmCampaign || null,
        });
        
        // User is signed in immediately - no email verification needed
        // Navigate to onboarding page
        navigate('/onboarding');
      } else {
        // Track successful signup with Refgrow (even if email verification required)
        if (typeof window !== 'undefined' && (window as any).Refgrow) {
          (window as any).Refgrow(0, 'signup', emailValidation.data);
        }

        // Track signup in our analytics
        await trackEvent({
          name: 'user_signup',
          properties: {
            signupMethod: 'email',
            userId: signUpData.user?.id,
          }
        });

        // Track Sign Up event in Mixpanel (even if email verification required)
        if (signUpData.user?.id) {
          safeMixpanelTrack('Sign Up', {
            user_id: signUpData.user.id,
            email: emailValidation.data,
            signup_method: 'email',
            utm_source: utmSource || null,
            utm_medium: utmMedium || null,
            utm_campaign: utmCampaign || null,
          });
        }
        
        // Email verification is required
        toast({
          title: "Account created!",
          description: "Please check your email to verify your account. Your 30-day trial has started!",
        });
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      
      // Check if this is an existing account error
      if (isExistingAccountError(error)) {
        const errorMessage = getAuthErrorMessage(error);
        
        // Use global error handler to prevent duplicate toasts
        if (errorHandler.shouldShowError(errorMessage)) {
          toast({
            title: getAuthErrorTitle(error),
            description: errorMessage,
            variant: "destructive",
          });
          
          // Redirect to login page with a helpful message
          navigate('/login?message=Please sign in with your existing account');
        }
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to create account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmitting || loading || googleLoading) {
      console.log("[SignUpForm] Google signup already in progress, ignoring duplicate submission");
      return;
    }
    
    console.log('Starting Google OAuth signup...');
    await signUpWithOAuth();
  };

  // Reset error handler on component mount to allow retries
  useEffect(() => {
    errorHandler.reset();
  }, []);

  // Debug: Log when dialog state changes
  useEffect(() => {
    if (showExistingAccountDialog) {
      console.log('Existing account dialog shown - user already has account');
    }
  }, [showExistingAccountDialog]);

  return (
    <div className={cn("", className)} {...props}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Create Account</h1>
          <p className="text-balance text-md text-muted-foreground">
            Start your 30-day free trial of ChronoCal Pro
          </p>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="fullName"
              rules={{ 
                required: "Full name is required",
                validate: (value) => {
                  const result = nameSchema.safeParse(value);
                  return result.success || "Invalid name format";
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <SecureInput placeholder="Enter your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              rules={{ 
                required: "Email is required",
                validate: (value) => {
                  const result = emailSchema.safeParse(value);
                  return result.success || "Invalid email format";
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <SecureInput type="email" placeholder="Enter your email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              rules={{ 
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters"
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
                  message: "Password must contain uppercase, lowercase, and number"
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <SecureInput type="password" placeholder="Create a password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-orange-600 hover:to-orange-500 hover:from-purple-500  text-white transition-all duration-0"
              disabled={loading || isSubmitting}
            >
              {(loading || isSubmitting) ? "Creating Account..." : "Start Free Trial"}
            </Button>
          </form>
        </Form>
        
        <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
          <span className="relative z-10 bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
        
        <Button
          variant="outline"
          type="button"
          className="w-full"
          onClick={handleGoogleSignUp}
          disabled={loading || isSubmitting || googleLoading}
        >
        <img 
          src="/assets/images/ui/google-g-logo.svg" 
          alt="Google" 
          className="w-6 h-6 mr-3"
        />
          {googleLoading ? "Connecting..." : "Sign up with Google"}
        </Button>
        
        <div className="text-center text-sm">
          Already have an account?{" "}
          <Link to="/login" className="text-primary underline underline-offset-4 hover:text-primary/80">
            Sign in
          </Link>
        </div>
      </div>

      <ExistingAccountDialog 
        open={showExistingAccountDialog}
        onOpenChange={closeDialog}
        onSignIn={handleExistingAccountSignIn}
      />
    </div>
  );
}
